import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useAISettings, useUpdateAISetting, useAIUsageSummary, useAIRecentLogs } from '@/hooks/useAISettings';
import { formatCents, MODEL_OPTIONS } from '@/lib/ai-cost';
import { formatDistanceToNow } from 'date-fns';
import type { AIFeature, AISettings, MatchCriteria, MessageDraftSettings, CriterionKey, CriterionRanking } from '@/types/ai';

const FEATURE_LABELS: Record<AIFeature, string> = {
  matching: 'Member Matching',
  document_parsing: 'Document Parsing',
  assistant: 'AI Assistant',
  message_drafting: 'Introduction Drafting',
  field_mapping: 'CSV Field Mapping',
};

export default function AdminAISettings() {
  const { data: settings, isLoading } = useAISettings();
  const updateSetting = useUpdateAISetting();
  const { data: usageSummary } = useAIUsageSummary();
  const { data: recentLogs } = useAIRecentLogs();
  const { toast } = useToast();

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!settings?.ai) {
    return <p className="text-muted-foreground p-8">AI settings not configured.</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold text-foreground">AI Settings</h1>
        <p className="text-muted-foreground text-sm">
          Configure AI features, monitor usage, and manage cost controls
        </p>
      </div>

      <Tabs defaultValue="usage">
        <TabsList>
          <TabsTrigger value="usage">Usage</TabsTrigger>
          <TabsTrigger value="features">Features</TabsTrigger>
          <TabsTrigger value="costs">Cost Controls</TabsTrigger>
          <TabsTrigger value="rates">Rate Limits</TabsTrigger>
          <TabsTrigger value="matching">Matching</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="usage" className="mt-6">
          <UsageSummaryTab summary={usageSummary || []} />
        </TabsContent>

        <TabsContent value="features" className="mt-6">
          <FeatureControlsTab
            settings={settings.ai}
            onUpdate={(updated) => {
              updateSetting.mutate(
                { key: 'ai_settings', value: updated },
                { onSuccess: () => toast({ title: 'Settings saved' }) }
              );
            }}
          />
        </TabsContent>

        <TabsContent value="costs" className="mt-6">
          <CostControlsTab
            settings={settings.ai}
            onUpdate={(updated) => {
              updateSetting.mutate(
                { key: 'ai_settings', value: updated },
                { onSuccess: () => toast({ title: 'Cost controls saved' }) }
              );
            }}
          />
        </TabsContent>

        <TabsContent value="rates" className="mt-6">
          <RateLimitsTab
            settings={settings.ai}
            onUpdate={(updated) => {
              updateSetting.mutate(
                { key: 'ai_settings', value: updated },
                { onSuccess: () => toast({ title: 'Rate limits saved' }) }
              );
            }}
          />
        </TabsContent>

        <TabsContent value="matching" className="mt-6">
          <MatchingCriteriaTab
            criteria={settings.matchCriteria}
            onUpdate={(updated) => {
              updateSetting.mutate(
                { key: 'match_criteria', value: updated },
                { onSuccess: () => toast({ title: 'Matching criteria saved' }) }
              );
            }}
          />
        </TabsContent>

        <TabsContent value="activity" className="mt-6">
          <RecentActivityTab logs={recentLogs || []} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Tab 1: Usage Summary ───────────────────────────────────────

function UsageSummaryTab({ summary }: { summary: import('@/types/ai').AIUsageSummaryRow[] }) {
  const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
  const thisMonth = summary.filter((r) => r.month === currentMonth);

  const totalRequests = thisMonth.reduce((s, r) => s + r.request_count, 0);
  const totalCost = thisMonth.reduce((s, r) => s + Number(r.total_cost_cents), 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          AI Usage — {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {thisMonth.length === 0 ? (
          <p className="text-sm text-muted-foreground">No AI usage recorded this month.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Feature</TableHead>
                <TableHead className="text-right">Requests</TableHead>
                <TableHead className="text-right">Input Tokens</TableHead>
                <TableHead className="text-right">Output Tokens</TableHead>
                <TableHead className="text-right">Est. Cost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {thisMonth.map((row) => (
                <TableRow key={row.feature}>
                  <TableCell className="font-medium">
                    {FEATURE_LABELS[row.feature as AIFeature] || row.feature}
                  </TableCell>
                  <TableCell className="text-right">{row.request_count.toLocaleString()}</TableCell>
                  <TableCell className="text-right">{row.total_input_tokens.toLocaleString()}</TableCell>
                  <TableCell className="text-right">{row.total_output_tokens.toLocaleString()}</TableCell>
                  <TableCell className="text-right">{formatCents(Number(row.total_cost_cents))}</TableCell>
                </TableRow>
              ))}
              <TableRow className="font-semibold border-t-2">
                <TableCell>Total</TableCell>
                <TableCell className="text-right">{totalRequests.toLocaleString()}</TableCell>
                <TableCell className="text-right">
                  {thisMonth.reduce((s, r) => s + r.total_input_tokens, 0).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  {thisMonth.reduce((s, r) => s + r.total_output_tokens, 0).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">{formatCents(totalCost)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Tab 2: Feature Controls ────────────────────────────────────

function FeatureControlsTab({
  settings,
  onUpdate,
}: {
  settings: AISettings;
  onUpdate: (updated: AISettings) => void;
}) {
  const [local, setLocal] = useState(settings);

  const updateFeature = (feature: AIFeature, field: string, value: unknown) => {
    const updated = {
      ...local,
      features: {
        ...local.features,
        [feature]: { ...local.features[feature], [field]: value },
      },
    };
    setLocal(updated);
  };

  return (
    <div className="space-y-4">
      {(Object.keys(FEATURE_LABELS) as AIFeature[]).map((feature) => {
        const config = local.features[feature];
        return (
          <Card key={feature}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium">{FEATURE_LABELS[feature]}</h3>
                <Switch
                  checked={config.enabled}
                  onCheckedChange={(v) => updateFeature(feature, 'enabled', v)}
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Model</Label>
                  <Select
                    value={config.model}
                    onValueChange={(v) => updateFeature(feature, 'model', v)}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MODEL_OPTIONS.map((m) => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Max Tokens</Label>
                  <Input
                    type="number"
                    value={config.max_tokens}
                    onChange={(e) => updateFeature(feature, 'max_tokens', parseInt(e.target.value) || 0)}
                    className="h-9"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Temperature: {config.temperature}</Label>
                  <Slider
                    value={[config.temperature]}
                    onValueChange={([v]) => updateFeature(feature, 'temperature', v)}
                    min={0}
                    max={1}
                    step={0.1}
                    className="mt-2"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
      <Button onClick={() => onUpdate(local)}>Save Feature Settings</Button>
    </div>
  );
}

// ─── Tab 3: Cost Controls ───────────────────────────────────────

function CostControlsTab({
  settings,
  onUpdate,
}: {
  settings: AISettings;
  onUpdate: (updated: AISettings) => void;
}) {
  const [local, setLocal] = useState(settings);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Monthly Limits</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label>API spend cap (dollars/month)</Label>
          <Input
            type="number"
            value={(local.monthly_spend_cap_cents / 100).toFixed(2)}
            onChange={(e) =>
              setLocal({ ...local, monthly_spend_cap_cents: Math.round(parseFloat(e.target.value || '0') * 100) })
            }
            className="w-40"
          />
        </div>
        <div className="space-y-2">
          <Label>Warning threshold: {local.warning_threshold_percent}%</Label>
          <Slider
            value={[local.warning_threshold_percent]}
            onValueChange={([v]) => setLocal({ ...local, warning_threshold_percent: v })}
            min={10}
            max={100}
            step={5}
            className="w-64"
          />
        </div>
        <div className="space-y-2">
          <Label>Alert email</Label>
          <Input
            type="email"
            value={local.alert_email || ''}
            onChange={(e) => setLocal({ ...local, alert_email: e.target.value || null })}
            placeholder="admin@example.com"
            className="w-64"
          />
        </div>
        <Button onClick={() => onUpdate(local)}>Save Cost Controls</Button>
      </CardContent>
    </Card>
  );
}

// ─── Tab 4: Rate Limits ─────────────────────────────────────────

function RateLimitsTab({
  settings,
  onUpdate,
}: {
  settings: AISettings;
  onUpdate: (updated: AISettings) => void;
}) {
  const [local, setLocal] = useState(settings);

  const updateLimit = (field: keyof AISettings['rate_limits'], value: number) => {
    setLocal({ ...local, rate_limits: { ...local.rate_limits, [field]: value } });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Rate Limits</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label>AI requests per minute</Label>
          <Input
            type="number"
            value={local.rate_limits.ai_requests_per_minute}
            onChange={(e) => updateLimit('ai_requests_per_minute', parseInt(e.target.value) || 0)}
            className="w-40"
          />
        </div>
        <div className="space-y-2">
          <Label>Batch operations per hour</Label>
          <Input
            type="number"
            value={local.rate_limits.batch_operations_per_hour}
            onChange={(e) => updateLimit('batch_operations_per_hour', parseInt(e.target.value) || 0)}
            className="w-40"
          />
        </div>
        <div className="space-y-2">
          <Label>Document uploads per hour</Label>
          <Input
            type="number"
            value={local.rate_limits.document_uploads_per_hour}
            onChange={(e) => updateLimit('document_uploads_per_hour', parseInt(e.target.value) || 0)}
            className="w-40"
          />
        </div>
        <Button onClick={() => onUpdate(local)}>Save Rate Limits</Button>
      </CardContent>
    </Card>
  );
}

// ─── Tab 5: Matching Criteria ───────────────────────────────────

const RANKABLE_CRITERIA: { key: CriterionKey; label: string; weightField: keyof MatchCriteria }[] = [
  { key: 'region', label: 'Region', weightField: 'location_weight' },
  { key: 'industry', label: 'Industry', weightField: 'industry_weight' },
  { key: 'skills', label: 'Skills / Function', weightField: 'skills_weight' },
  { key: 'interests', label: 'Interests', weightField: 'mutual_benefit_weight' },
];

function rankingsToWeights(rankings: CriterionRanking[]): Partial<MatchCriteria> {
  const active = rankings.filter((r) => r.rank > 0).sort((a, b) => a.rank - b.rank);
  const weightMap: Record<number, number> = { 1: 40, 2: 30, 3: 20, 4: 10 };
  // Proportional if fewer than 4
  const proportional: Record<number, Record<number, number>> = {
    1: { 1: 100 },
    2: { 1: 60, 2: 40 },
    3: { 1: 45, 2: 35, 3: 20 },
    4: { 1: 40, 2: 30, 3: 20, 4: 10 },
  };
  const weights = proportional[active.length] || weightMap;

  const result: Partial<MatchCriteria> = {
    location_weight: 0,
    industry_weight: 0,
    skills_weight: 0,
    mutual_benefit_weight: 0,
  };

  for (const r of active) {
    const def = RANKABLE_CRITERIA.find((c) => c.key === r.key);
    if (def) {
      (result as any)[def.weightField] = weights[r.rank] || 0;
    }
  }

  return result;
}

export function MatchingCriteriaTab({
  criteria,
  onUpdate,
}: {
  criteria: MatchCriteria;
  onUpdate: (updated: MatchCriteria) => void;
}) {
  const [local, setLocal] = useState(criteria);

  // Initialize rankings from saved criteria or from existing weights
  const [rankings, setRankings] = useState<CriterionRanking[]>(() => {
    if (criteria.criteria_ranking && criteria.criteria_ranking.length > 0) {
      return criteria.criteria_ranking;
    }
    // Derive from existing weights
    const entries: { key: CriterionKey; weight: number }[] = [
      { key: 'region', weight: criteria.location_weight },
      { key: 'industry', weight: criteria.industry_weight },
      { key: 'skills', weight: criteria.skills_weight },
      { key: 'interests', weight: criteria.mutual_benefit_weight },
    ];
    const sorted = entries
      .filter((e) => e.weight > 0)
      .sort((a, b) => b.weight - a.weight);
    return RANKABLE_CRITERIA.map((c) => {
      const idx = sorted.findIndex((s) => s.key === c.key);
      return { key: c.key, rank: idx >= 0 ? idx + 1 : 0 };
    });
  });

  const [expLevelEnabled, setExpLevelEnabled] = useState(criteria.experience_level_weight > 0);

  const toggleCriterion = (key: CriterionKey) => {
    setRankings((prev) => {
      const existing = prev.find((r) => r.key === key);
      if (!existing) return prev;
      if (existing.rank > 0) {
        // Disable: set rank to 0 and recompact
        const updated = prev.map((r) => (r.key === key ? { ...r, rank: 0 } : r));
        return recompactRanks(updated);
      } else {
        // Enable: add at next available rank
        const maxRank = Math.max(0, ...prev.map((r) => r.rank));
        return prev.map((r) => (r.key === key ? { ...r, rank: maxRank + 1 } : r));
      }
    });
  };

  const setRank = (key: CriterionKey, newRank: number) => {
    setRankings((prev) => {
      const updated = prev.map((r) => {
        if (r.key === key) return { ...r, rank: newRank };
        // Swap: if another criterion has this rank, give it the old rank
        if (r.rank === newRank) {
          const oldRank = prev.find((p) => p.key === key)?.rank || 0;
          return { ...r, rank: oldRank };
        }
        return r;
      });
      return updated;
    });
  };

  const recompactRanks = (items: CriterionRanking[]): CriterionRanking[] => {
    const active = items.filter((r) => r.rank > 0).sort((a, b) => a.rank - b.rank);
    const inactive = items.filter((r) => r.rank === 0);
    return [
      ...active.map((r, i) => ({ ...r, rank: i + 1 })),
      ...inactive,
    ];
  };

  const handleSave = () => {
    const weightUpdates = rankingsToWeights(rankings);
    const updated: MatchCriteria = {
      ...local,
      ...weightUpdates,
      experience_level_weight: expLevelEnabled ? (local.experience_level_weight || 15) : 0,
      criteria_ranking: rankings,
    };
    onUpdate(updated);
  };

  const activeRankings = rankings.filter((r) => r.rank > 0).sort((a, b) => a.rank - b.rank);
  const activeCount = activeRankings.length;

  const toggles: { key: keyof MatchCriteria; label: string }[] = [
    { key: 'prioritize_same_industry', label: 'Prioritize same industry' },
    { key: 'prioritize_complementary_skills', label: 'Prioritize complementary skills' },
    { key: 'prioritize_same_location', label: 'Prioritize same location' },
    { key: 'allow_same_company', label: 'Allow same company' },
    { key: 'include_admin_notes', label: 'Include admin notes in matching' },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Criteria Ranking</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground mb-4">
            Toggle criteria on/off and set their priority rank. Weights are calculated automatically from rank order.
          </p>
          {RANKABLE_CRITERIA.map((criterion) => {
            const ranking = rankings.find((r) => r.key === criterion.key);
            const isActive = ranking && ranking.rank > 0;
            const computedWeights = rankingsToWeights(rankings);
            const weight = (computedWeights as any)[criterion.weightField] || 0;

            return (
              <div key={criterion.key} className="flex items-center gap-4 py-2 border-b last:border-0">
                <Switch
                  checked={!!isActive}
                  onCheckedChange={() => toggleCriterion(criterion.key)}
                />
                <span className="text-sm font-medium w-32">{criterion.label}</span>
                {isActive && (
                  <>
                    <Select
                      value={String(ranking!.rank)}
                      onValueChange={(v) => setRank(criterion.key, parseInt(v))}
                    >
                      <SelectTrigger className="w-20 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: activeCount }, (_, i) => (
                          <SelectItem key={i + 1} value={String(i + 1)}>#{i + 1}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-muted-foreground">{weight}%</span>
                  </>
                )}
              </div>
            );
          })}
          <div className="flex items-center gap-4 py-2 border-t">
            <Switch
              checked={expLevelEnabled}
              onCheckedChange={setExpLevelEnabled}
            />
            <span className="text-sm font-medium w-32">Experience Level</span>
            {expLevelEnabled && (
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground">Weight:</Label>
                <Slider
                  value={[local.experience_level_weight]}
                  onValueChange={([v]) => setLocal({ ...local, experience_level_weight: v })}
                  min={0}
                  max={50}
                  step={5}
                  className="w-32"
                />
                <span className="text-xs text-muted-foreground">{local.experience_level_weight}%</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Preferences</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {toggles.map(({ key, label }) => (
            <div key={key} className="flex items-center justify-between">
              <Label>{label}</Label>
              <Switch
                checked={local[key] as boolean}
                onCheckedChange={(v) => setLocal({ ...local, [key]: v })}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Thresholds</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Minimum match score (0–100)</Label>
            <Input
              type="number"
              value={local.min_score_threshold}
              onChange={(e) => setLocal({ ...local, min_score_threshold: parseInt(e.target.value) || 0 })}
              min={0}
              max={100}
              className="w-32"
            />
          </div>
          <div className="space-y-2">
            <Label>Max matches per member (1–20)</Label>
            <Input
              type="number"
              value={local.max_matches_per_member}
              onChange={(e) => setLocal({ ...local, max_matches_per_member: parseInt(e.target.value) || 1 })}
              min={1}
              max={20}
              className="w-32"
            />
          </div>
          <div className="space-y-2">
            <Label>Explanation detail</Label>
            <Select
              value={local.explanation_detail}
              onValueChange={(v) => setLocal({ ...local, explanation_detail: v as MatchCriteria['explanation_detail'] })}
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="brief">Brief (1 sentence)</SelectItem>
                <SelectItem value="standard">Standard (2-3 sentences)</SelectItem>
                <SelectItem value="detailed">Detailed (paragraph)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Custom AI guidance</Label>
            <Textarea
              value={local.custom_guidance || ''}
              onChange={(e) => setLocal({ ...local, custom_guidance: e.target.value || null })}
              placeholder="Additional instructions for the AI matching engine..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave}>Save Matching Criteria</Button>
    </div>
  );
}

// ─── Tab 6: Recent Activity ─────────────────────────────────────

function RecentActivityTab({ logs }: { logs: (import('@/types/ai').AIUsageLog & { actor_name?: string | null })[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Recent AI Activity</CardTitle>
      </CardHeader>
      <CardContent>
        {logs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No recent AI activity.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Feature</TableHead>
                <TableHead>User</TableHead>
                <TableHead className="text-right">Tokens</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                  </TableCell>
                  <TableCell className="text-sm">
                    {FEATURE_LABELS[log.feature as AIFeature] || log.feature}
                  </TableCell>
                  <TableCell className="text-sm">{log.actor_name || '—'}</TableCell>
                  <TableCell className="text-right text-sm">
                    {(log.input_tokens + log.output_tokens).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right text-sm">{formatCents(Number(log.cost_cents))}</TableCell>
                  <TableCell className="text-sm">
                    {log.status === 'success' ? 'OK' : log.status}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
